import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import GenericForm, { FormField } from "../../../components/GenericForm";
import { useQuery } from "@tanstack/react-query";
import { useAlert } from "../../../common/contexts/AlertContext";
import { creatSite, fetchSiteAdmins } from "../../../common/api/siteService";
import PopUp from "../../../components/PatientCharts/PopUp";
import Loader from "../../../components/Loader/Loader";

const CreateSite = () => {
  const location = useLocation();
  const site = location.state?.user;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const alert = useAlert();
  const [showPopup, setShowPopup] = useState(false);
  const [pendingSwitch, setPendingSwitch] = useState<{
    fieldName: string;
    checked: boolean;
  } | null>(null);
  const getText = (text: string) => t(`createSite.${text}`);
  const [useNewAdmin, setUseNewAdmin] = useState(false);
  const baseFields: FormField[] = [
    { label: "Center Name*", name: "centerName", type: "text" },
    { label: "Country/Region*", name: "country", type: "text" },
    { label: "City*", name: "city", type: "text" },
    { label: "Street*", name: "street", type: "text" },
    { label: "Apt, suite, etc (optional)", name: "apartment", type: "text" },
    { label: "ZIP/Postcode*", name: "zipCode", type: "text" },
    {
      label: "Active / Non-Active",
      name: "centerStatus",
      type: "switch",
      value: site?.centerStatus === "Active" ? true : true
    },
  ];
  const initialAdminField: FormField = {
    label: "Related Admin*",
    name: "relatedAdmin",
    type: "select",
    options: [],
  };
  const [fields, setFields] = useState<FormField[]>([
    ...baseFields,
    initialAdminField,
  ]);
  const {
    isLoading: isAdminsLoading,
    error: adminsError,
    data: adminsResponse,
  } = useQuery({
    queryKey: ["siteAdmins"],
    queryFn: fetchSiteAdmins,
  });
  useEffect(() => {
    if (!useNewAdmin && adminsResponse) {
      const dataArray = Array.isArray(adminsResponse) ? adminsResponse : [];
      const adminOptions = [
        { label: "Add New Admin", value: "new_admin" },
        ...dataArray.map(
          (admin: {
            email: string;
            firstName: string;
            lastName: string;
          }) => ({
            label: `${admin.firstName} ${admin.lastName}`,
            value: admin.email,
          })
        ),
      ];
      setFields((prevFields) =>
        prevFields.map((field) =>
          field.name === "relatedAdmin"
            ? { ...field, options: adminOptions }
            : field
        )
      );
    }
  }, [adminsResponse, useNewAdmin]);
  const handleFieldChange = (fieldName: string, value: any) => {
    if (fieldName === "relatedAdmin" && value === "new_admin") {
      setUseNewAdmin(true);
      setFields([
        ...baseFields,
        { label: "Contact Admin", name: "contactSection", type: "section" },
        { label: "First Name*", name: "contactFirstName", type: "text" },
        { label: "Last Name*", name: "contactLastName", type: "text" },
        { label: "Email*", name: "contactEmail", type: "email" },
        { label: "Phone Number*", name: "contactPhone", type: "phoneInput" },
      ]);
    }
  };

  const handleSwitchChange = (fieldName: string, checked: boolean) => {
    if (fieldName === "centerStatus" && checked) {
      // Show the pop-up to confirm enabling the switch
      setPendingSwitch({ fieldName, checked });
      setShowPopup(true);
    } else {
      // Directly update the field's value in the formData
      updateFieldValue(fieldName, checked);
    }
  };

  const handleConfirm = () => {
    if (pendingSwitch) {
      updateFieldValue(pendingSwitch.fieldName, pendingSwitch.checked);
    }
    setShowPopup(false);
    setPendingSwitch(null);
  };

  const handleCancel = () => {
    setShowPopup(false);
    setPendingSwitch(null);
  };

  const updateFieldValue = (fieldName: string, value: boolean) => {
    setFields((prevFields) =>
      prevFields.map((field) =>
        field.name === fieldName ? { ...field, value } : field
      )
    );
  };

  const handleSubmit = async (formData: { [key: string]: any }) => {
    console.log("Form submitted with data:", formData);
    const centerStatusAsString = formData.centerStatus ? "Active" : "Inactive";
    let siteAdminEmail: string;
    let siteAdminFirstName: string;
    let siteAdminLastName: string;

    if (useNewAdmin) {
      siteAdminEmail = formData.contactEmail;
      siteAdminFirstName = formData.contactFirstName;
      siteAdminLastName = formData.contactLastName;
    } else {
      siteAdminEmail = formData.relatedAdmin;
      const selectedAdmin = adminsResponse.find(
        (admin: { email: string }) => admin.email === formData.relatedAdmin
      );
      siteAdminFirstName = selectedAdmin.firstName;
      siteAdminLastName = selectedAdmin.lastName
    }
    const res = await creatSite({
      siteName: formData.centerName,
      country: formData.country,
      city: formData.city,
      street: formData.street,
      apartment: formData.apartment,
      zipCode: formData.zipCode,
      centerStatus: centerStatusAsString,
      siteAdminEmail,
      siteAdminFirstName,
      siteAdminLastName,
    });
    console.log("Payload sent to API:", res);

    if (res.success) {
      alert("Site created successfully", "msg");
      navigate(-1);
    } else {
      alert(res.err, "error");
    }
  };

  if (isAdminsLoading) {
    return <Loader text={getText("Loading Admins...")} />;
  }

  if (adminsError) {
    return <div>{t("createSite.Error fetching centers")}</div>;
  }

  return (
    <>
      <GenericForm
        backButtonAction={() => navigate(-1)}
        backButtonText={getText("Back")}
        buttonText={getText("Save_changes")}
        fields={fields}
        onSubmit={handleSubmit}
        title={getText("New_Center")}
        onSwitchChange={handleSwitchChange}
        onFieldChange={handleFieldChange}
      />
      {showPopup && (
        <PopUp
          message={getText("popUp_message")}
          open={showPopup}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </>
  );
};

export default CreateSite;
